import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import {
  aiPlanFilterBarSx,
  aiPlanFilterFieldSx,
} from "../../../constants/regulationDetailsNavTabStyles";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { formatConceptCodeToLabel } from "../../../services/autoTaggingService";

type TaggingPlanFilterBarProps = Readonly<{
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  onObjectTypeChange: (objectType: string, objectSubType: string) => void;
  onObjectSubTypeChange: (objectSubType: string) => void;
  onConceptCodeChange: (conceptCode: string) => void;
}>;

export default function TaggingPlanFilterBar({
  objectType,
  objectSubType,
  conceptCode,
  onObjectTypeChange,
  onObjectSubTypeChange,
  onConceptCodeChange,
}: TaggingPlanFilterBarProps) {
  const {
    objectTypeOptions,
    conceptOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
  } = useAssessmentPlanFilterOptions(objectType, objectSubType);

  // Empty Object Type → all sub-types across types.
  const objectSubTypeOptions = getObjectSubTypeOptions(objectType);
  // Always show Sub-Type by default; hide only when a type has no sub-types (e.g. RTQ).
  const showObjectSubType = !objectType || objectSubTypeOptions.length > 0;

  return (
    <Box sx={aiPlanFilterBarSx}>
      <TextField
        select
        size="small"
        label="Object Type"
        value={objectType}
        disabled={planTypesLoading}
        sx={aiPlanFilterFieldSx}
        onChange={(event) => {
          // Maps to API `target_type`. Changing type resets `sub_type` to All.
          onObjectTypeChange(event.target.value, "");
        }}
        SelectProps={{
          MenuProps: {
            PaperProps: { sx: { maxHeight: 300 } },
          },
        }}
      >
        <MenuItem value="">All</MenuItem>
        {objectTypeOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>

      {showObjectSubType ? (
        <TextField
          select
          size="small"
          label="Object Sub-Type"
          value={objectSubType}
          sx={aiPlanFilterFieldSx}
          onChange={(event) => onObjectSubTypeChange(event.target.value)}
          SelectProps={{
            MenuProps: {
              PaperProps: { sx: { maxHeight: 300 } },
            },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {objectSubTypeOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      <TextField
        select
        size="small"
        label="Dimension"
        value={conceptCode}
        sx={aiPlanFilterFieldSx}
        onChange={(event) => onConceptCodeChange(event.target.value)}
        SelectProps={{
          MenuProps: {
            PaperProps: { sx: { maxHeight: 300 } },
          },
        }}
      >
        <MenuItem value="">All</MenuItem>
        {conceptOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {formatConceptCodeToLabel(option.code)}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
